/**
 * PrintProblemAlert
 * =================
 * Listener globale: gestisce SOLO errori CRITICI di configurazione/persistenti.
 *
 * ⚠️ Cambio importante (v5.5): la maggior parte degli errori di stampa NON sono
 * più mostrati come popup toast (richiesta utente: "sono fastidiosi"). Il feedback
 * visivo è ora direttamente sul bottone "Invia in cucina" del terminale che ha
 * inviato la comanda (vedi `OrderEditor.jsx` — il bottone lampeggia rosso se la
 * stampa fallisce, verde se va a buon fine).
 *
 * QUESTO COMPONENTE mantiene popup SOLO per:
 *  - `no_printer`: nessuna stampante configurata per la stazione (errore di setup,
 *    non recuperabile automaticamente, l'utente deve agire in Stampanti)
 *  - `print_problem_resolved`: stampa arrivata in ritardo, conferma success silenziosa
 *
 * Gli altri errori (`dispatch_failed`, `browser_print_failed`, `no_browser_ack`)
 * sono ora gestiti come stato del bottone Invia, non come toast.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useWS } from "@/contexts/WebSocketContext";
import { Printer } from "lucide-react";

const PrintProblemAlert = () => {
    const { subscribe } = useWS() || {};
    const shownRef = useRef(new Set());

    useEffect(() => {
        if (!subscribe) return;
        const unsub = subscribe((msg) => {
            // Stampa arrivata in ritardo → conferma silenziosa
            if (msg?.type === "print_problem_resolved" && msg.order_id) {
                toast.success(
                    `🖨️ Stampa confermata${msg.printer_name ? ` su ${msg.printer_name}` : ""} (arrivata in ritardo)`,
                    { duration: 4000 }
                );
                return;
            }
            if (msg?.type !== "print_problem") return;
            const errors = msg.errors || [];
            const firstReason = errors[0]?.reason;
            // ⚠️ Mostra popup SOLO per errori critici di configurazione
            // (gli altri sono gestiti dal bottone Invia con stato)
            const isCritical = firstReason === "no_printer";
            if (!isCritical) return;

            const tableLabel = msg.table_label || "";
            const summary = errors.length === 1
                ? errors[0].message
                : `${errors.length} stampanti in errore`;
            const failedDestinations = [...new Set(errors.map((e) => e.destination).filter(Boolean))];
            const dedupKey = `${msg.order_id || "noid"}|${failedDestinations.sort().join(",")}|${Date.now() / 30000 | 0}`;
            if (shownRef.current.has(dedupKey)) return;
            shownRef.current.add(dedupKey);
            setTimeout(() => shownRef.current.delete(dedupKey), 30000);

            const toastId = `print-problem-${msg.order_id || dedupKey}`;
            toast.error(
                `🖨️ Stampante mancante${tableLabel ? ` · ${tableLabel}` : ""}: ${summary}`,
                {
                    id: toastId,
                    duration: 15000,
                    description: failedDestinations.length === 1
                        ? `Stazione ${failedDestinations[0]} senza stampante configurata. Vai in Stampanti per aggiungerla.`
                        : "Configura le stampanti delle stazioni in 'Stampanti'.",
                    icon: <Printer className="w-5 h-5 text-red-400" />,
                    closeButton: true,
                }
            );
        });
        return unsub;
    }, [subscribe]);

    return null;
};

export default PrintProblemAlert;
